import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, UrlSegment} from '@angular/router';
import {MatSnackBar} from '@angular/material/snack-bar';
import {InspectionFormsService} from '../../../../../data/inspection-forms/inspection-forms.service';
import {DvirFormReviewingSignaturesOption} from '../model/form-options/DvirFormReviewingSignaturesOption';
import {DvirFormQuestionFormatOption} from '../model/form-options/DvirFormQuestionFormatOption';
import {debounce, firstValueFrom, interval, of, Subject, Subscription, switchMap} from 'rxjs';
import {SharedDataService} from '../../../../../shared/services/shared-data.service';
import {DvirFormDto, DvirFormEmailNotificationType} from '../model/dto/DvirFormDto';
import {UserManagementService} from '../../../../../data/users/user-management.service';
import {MatCheckbox, MatCheckboxChange} from '@angular/material/checkbox';
import {FeatureFlagEnum} from '../../../../../shared/models/configuration.model';
import {ConfigurationService} from '../../../../../configuration/configuration.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatError, MatFormField} from '@angular/material/form-field';
import {AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {CardComponent} from '../../../../../shared/components/card/card.component';
import {CardContentRawDirective} from '../../../../../shared/components/card/card-content.directive';
import {RowWithToggleComponent} from '../../row-with-toggle/row-with-toggle.component';
import {RowWithSelectComponent} from '../../row-with-select/row-with-select.component';
import {catchError, tap} from 'rxjs/operators';
import {DvirForm} from '../model/dvir-form';
import {MatDialog} from '@angular/material/dialog';
import {RootRoute, SettingsRoute} from '../../../../../shared/models/angular-routing';
import {MatInput} from '@angular/material/input';
import {
  ConfirmationDialogComponent
} from '../../../../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import {SetFocusOnLoadDirective} from '../../../../../shared/directives/set-focus-on-load.directive';
import {MatDivider} from '@angular/material/divider';

@Component({
    selector: 'app-inspection-form',
    templateUrl: './inspection-form.component.html',
  imports: [
    MatProgressSpinner,
    FormsModule,
    MatButton,
    MatRadioGroup,
    MatRadioButton,
    MatCheckbox,
    CardComponent,
    MatError,
    RowWithToggleComponent,
    RowWithSelectComponent,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    SetFocusOnLoadDirective,
    CardContentRawDirective,
    MatDivider
  ],
  styleUrls: ['./inspection-form.component.scss',
    '../../../../settings/settings-fields.scss',
    '../../../../settings/settings-common.scss']
})
export class InspectionFormComponent implements OnInit, OnDestroy {

  loading = false;
  loadError: string | undefined;

  saving = false;
  saveError: any;
  inspectionFormSaveTopic: Subject<DvirForm>;

  formDetailHeader = 'Inspection form';

  inspectionForm: DvirForm = null;
  notificationUsers: { email: string, selected: boolean }[] = [];

  // to check if cartegraph is enabled for tenant
  cartegraphEnabled: boolean;

  DvirFormReviewingSignaturesOption = DvirFormReviewingSignaturesOption;
  DvirFormQuestionFormatOption = DvirFormQuestionFormatOption;

  dvirFormEmailNotificationTypes = [
    {
      label: 'Level 1',
      description: 'If a vehicle is taken out of service',
      value: DvirFormEmailNotificationType.ON_OUT_OF_SERVICE
    },
    {
      label: 'Level 2',
      description: 'If a defect is marked on an inspection or a vehicle is taken out of service',
      value: DvirFormEmailNotificationType.ON_DEFECT
    },
    {
      label: 'Level 3',
      description: 'All completed inspection forms',
      value: DvirFormEmailNotificationType.ALL
    }
  ];

  formGroup: FormGroup;

  private readonly subscriptions: Subscription[] = [];
  private goToQuestionsAfterSave = false;

  RootRoute = RootRoute;
  SettingsRoute = SettingsRoute;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private readonly inspectionFormService: InspectionFormsService,
    private readonly sharedDataService: SharedDataService,
    private readonly userService: UserManagementService,
    private readonly configurationService: ConfigurationService,
    private readonly fb: FormBuilder,
  ) {
    this.createFormGroup();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  ngOnInit(): void {
    // this.createFormGroup();
    // this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions.push(
      this.route.url
        .subscribe((urlSegments) => {
          this.loading = true;
          delete this.loadError;
          const inspectionFormIdParam = this.route.snapshot.paramMap.get('inspectionFormId');

          if (inspectionFormIdParam === 'new') {
            this.initNew();
          } else if (inspectionFormIdParam === 'from-template') {
            this.initDefault();
          } else {
            this.initCopyOrEdit(+inspectionFormIdParam, urlSegments);
          }
        })
    );

    this.subscriptions.push(
      this.configurationService.sharedConfigurationModel.subscribe(cfg => {
        this.cartegraphEnabled = cfg?.hasFeatureFlag(FeatureFlagEnum.CartegraphIntegration);
      }));

    this.inspectionFormSaveTopic = new Subject();

    this.subscriptions.push(
      this.inspectionFormSaveTopic.pipe(
        tap(x => {
          this.saving = true;
          this.formGroup.disable();
        }), // call saving indicator earlier before debounce time
        debounce(form => interval(form.id !== undefined ? 500 : 0)),
        switchMap(form => {
          this.saving = true;
          return this.inspectionFormService.saveForm(form.toDto()).pipe(
            catchError(error => {
              this.saveError = error;
              return of();
            })
          );
        })
      ).subscribe((form) => {
        if (form.id !== this.inspectionForm.id) {
          if (this.goToQuestionsAfterSave) {
            this.router.navigate([`/${RootRoute.SETTINGS}`, SettingsRoute.INSPECTION_FORMS, form.id, 'questions'])
              .then();
          } else {
            this.router.navigate([`/${RootRoute.SETTINGS}`, SettingsRoute.INSPECTION_FORMS, form.id])
              .then(() => {
                this.saving = false;
                this.formGroup.enable({ emitEvent: false });
              });
          }
        } else {
          this.setInspectionForm(DvirForm.fromDto(form));
          this.saving = false;
          this.formGroup.enable({ emitEvent: false });
        }
      })
    );

    // load all supervisors and admins as potential email recipients
    this.userService.getSupervisorList()
      .then(response => {
        this.notificationUsers = response.data
          .filter(user => !user.email.startsWith('neotreks_support@'))
          .map(user => {
            return {email: user.email, selected: false};
          });
      });
  }

  navigateBack() {
    const errors = Object.keys(this.formGroup.errors || [])?.join(',');
    const shouldNavigate = this.formGroup.invalid ?
      firstValueFrom(this.dialog.open(ConfirmationDialogComponent, {
        data:
          {
            title: 'Confirm discarding of unsaved changes',
            text: `The form contains invalid data <strong>(${errors})</strong> and cannot be saved.<br/>By leaving you'll lose any changes. <br/> Are you sure?`,
            yesLabel: 'Yes, discard the changes'
          }
      }).afterClosed()) : Promise.resolve(true);

    shouldNavigate.then(navigate => {
      // prevent 'Form submission canceled because the form is not connected'
      setTimeout(() => {
        if (navigate) {
          this.router.navigate([`/${RootRoute.SETTINGS}`, SettingsRoute.INSPECTION_FORMS])
            .then();
        }
      }, 0);
    });
  }

  triggerInspectionFormSave() {
    if (this.formGroup.valid) {
      const {
        title,
        hasRemarks,
        requireSignatures,
        reviewingSignatures,
        enableEmailNotifications,
        emailNotificationType,
        questionFormat,
      } = this.formGroup.value;

      const formToSave = Object.assign(
        Object.create(this.inspectionForm),
        {
          title,
          hasRemarks,
          reviewingSignatures,
          questionFormat,
          settings: {
            enableEmailNotifications,
            emailNotificationType,
            emailRecipients: enableEmailNotifications ? this.notificationUsers
              ?.filter(user => user.selected)
              ?.map(user => user.email) || [] : []
          }
        }
      );
      // handle signatures save
      if (!requireSignatures) {
        formToSave.reviewingSignatures = DvirFormReviewingSignaturesOption.NEVER_REQUIRED;
      }
      if (!enableEmailNotifications) {
        delete formToSave.settings.emailNotificationType;
      }

      this.inspectionFormSaveTopic.next(formToSave);
    }
  }

  notificationUsersChanged(user: { email: string; selected: boolean }, $event: MatCheckboxChange) {
    user.selected = $event.checked;
    this.formGroup.updateValueAndValidity();
    this.triggerInspectionFormSave();
  }

  editQuestions() {
    if (this.inspectionForm.id === undefined) {
      this.goToQuestionsAfterSave = true;
      this.triggerInspectionFormSave();
    } else {
      this.router.navigate([`/${RootRoute.SETTINGS}`, SettingsRoute.INSPECTION_FORMS, this.inspectionForm.id, 'questions'])
        .then();
    }
  }

  private initNew() {
    this.setInspectionForm(DvirForm.createNew());
    this.formDetailHeader = 'Create new inspection form';
    this.loading = false;
  }

  private initDefault() {
    const selectedSystemForm = this.sharedDataService.takeOut('dvir-form-template') as DvirFormDto;
    if (selectedSystemForm != null) {
      this.setInspectionForm(DvirForm.fromDto(selectedSystemForm));
      this.loading = false;
    } else {
      this.initNew(); // this is fallback for reload page or after the re-login
    }
  }

  private initCopyOrEdit(inspectionFormIdParam, urlSegments: UrlSegment[]) {
    const inspectionFormId = +inspectionFormIdParam;
    this.inspectionFormService.getForm(inspectionFormId)
      .then(response => {
        if (urlSegments[urlSegments.length - 1].path === 'copy') {
          delete response.journalId;
          delete response.id;
          response.title += ' (copy)';
        }
        this.setInspectionForm(DvirForm.fromDto(response));
        this.loading = false;
      })
      .catch(error => {
        this.handleLoadError('Error while loading inspection form', error);
        this.inspectionForm = null;
      });
  }

  private setInspectionForm(form: DvirForm) {
    this.inspectionForm = form;

    const reviewingSignatures = this.inspectionForm.reviewingSignatures;
    const requireSignatures = reviewingSignatures.code !== DvirFormReviewingSignaturesOption.NEVER_REQUIRED.code;

    const { title, questionFormat, hasRemarks} = form;
    const { enableEmailNotifications, emailNotificationType, emailRecipients} = form.settings;
    const notificationUsersValid = this.notificationUsers.filter(
      user => emailRecipients?.indexOf(user.email) !== -1).length > 0;

    this.formGroup.setValue({
      title: title || '',
      questionFormat,
      hasRemarks,
      requireSignatures,
      reviewingSignatures,
      enableEmailNotifications: enableEmailNotifications && notificationUsersValid && emailRecipients?.length > 0,
      emailNotificationType: emailNotificationType || null,
    }, {emitEvent: false});
    this.notificationUsers.forEach(user => {
      user.selected = (emailRecipients || []).indexOf(user.email) !== -1;
    });
  }

  private showSnackBar(message: string) {
    this.snackBar.open(message, null, {duration: 2000});
  }

  private handleLoadError(msg: string, error: any) {
    const msgError = `${msg} :: ${error}`;

    console.error(msgError);
    this.showSnackBar(msg);

    this.loading = false;
    this.loadError = msgError;
  }

  private createFormGroup() {
    this.formGroup = this.fb.group({
      title: this.fb.control('', {
        validators: Validators.required,
        updateOn: 'blur',
      }),
      questionFormat: this.fb.control(undefined),
      hasRemarks: this.fb.control(undefined),
      requireSignatures: this.fb.control(undefined),
      reviewingSignatures: this.fb.control(undefined),
      enableEmailNotifications: this.fb.control(undefined),
      emailNotificationType: this.fb.control(undefined, {nonNullable: false}),
    }, {
      validators: [(control) => this.validateEmailRecipients(control)]
    });

    this.subscriptions.push(
      this.formGroup.controls.requireSignatures.valueChanges.subscribe(value => {
        if (value) {
          this.formGroup.controls.reviewingSignatures.setValue(DvirFormReviewingSignaturesOption.ALWAYS_REQUIRED, {emitEvent: false});
        }
      })
    );

    this.subscriptions.push(
      this.formGroup.controls.enableEmailNotifications.valueChanges.subscribe(value => {
        if (value) {
          this.formGroup.controls.emailNotificationType.setValue(DvirFormEmailNotificationType.ON_OUT_OF_SERVICE, {emitEvent: false});
        }
      })
    );

    this.subscriptions.push(
      this.formGroup.valueChanges.subscribe(_ => {
        this.triggerInspectionFormSave();
      })
    );
  }

  validateEmailRecipients(control: AbstractControl) {
    const { value : {enableEmailNotifications} } = control;
    if (!enableEmailNotifications) {
      return null;
    }
    if ((this?.notificationUsers?.find(user => user.selected))) {
      return null;
    }

    return {missingRecipient: true};
  }

}
